import { useQuery } from '@tanstack/react-query';
import { taxRatesAPI, TaxRate } from '../utils/taxRatesAPI';
import { useAuth } from './useAuth';

/**
 * Hook pour gérer les taux de TVA
 * Responsabilité : Charger, cacher et fournir les taux de TVA
 * Migrated to TanStack Query for automatic caching and refetching
 */
export function useTaxRates() {
  const { user } = useAuth();
  const tenantId = user?.tenantId;
  const companyId = user?.companyId;

  const {
    data: taxRates = [],
    isLoading: loading,
    error: queryError,
  } = useQuery<TaxRate[], Error>({
    queryKey: ['tax-rates', tenantId, companyId, { activeOnly: true }],
    queryFn: async () => {
      const response = await taxRatesAPI.getAll({ activeOnly: true });
      return response.taxRates;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000, // Tax rates rarely change, cache 5 minutes
  });

  const error = queryError ?? null;

  // Trouver le taux par défaut
  const defaultTaxRate = taxRates.find(tr => tr.isDefault) ||
                         taxRates.find(tr => Number(tr.rate) === 20) ||
                         taxRates[0]; // Fallback sur le premier si aucun par défaut

  // Trouver un taux par ID
  const getTaxRateById = (id: string): TaxRate | undefined => {
    return taxRates.find(tr => tr.id === id);
  };

  // Trouver un taux par son pourcentage
  const getTaxRateByPercent = (percent: number): TaxRate | undefined => {
    return taxRates.find(tr => Number(tr.rate) === percent);
  };

  return {
    taxRates,
    loading,
    error,
    defaultTaxRate,
    getTaxRateById,
    getTaxRateByPercent,
  };
}
